// apps/web/src/components/DashboardLayout.tsx
// Plan 4a — authenticated-route layout wrapper. Refactored from the
// previous sidebar layout to a minimal top nav: Logo + Dashboard +
// Library + Account dropdown. The legacy nav items (Courses, Learning,
// Explore, Marketplace, etc.) are removed entirely. Legacy routes
// remain reachable by direct URL and will be deleted in Plan 4b.

import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc' },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 24px',
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: 28 },
  logo: {
    fontSize: 17,
    fontWeight: 800,
    color: '#1f2937',
    textDecoration: 'none',
    letterSpacing: '-0.01em',
  },
  navLinks: { display: 'flex', alignItems: 'center', gap: 22 },
  link: {
    color: '#4b5563',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 600,
    padding: '8px 0',
    borderBottom: '3px solid transparent',
  },
  linkActive: {
    color: '#059669',
    borderBottomColor: '#059669',
  },
  account: { position: 'relative' },
  accountBtn: {
    background: 'transparent',
    border: 'none',
    color: '#1f2937',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  menu: {
    position: 'absolute',
    right: 0,
    top: '100%',
    marginTop: 6,
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
    minWidth: 180,
    overflow: 'hidden',
    zIndex: 20,
  },
  menuItem: {
    display: 'block',
    padding: '10px 14px',
    color: '#1f2937',
    textDecoration: 'none',
    fontSize: 14,
    background: 'transparent',
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    cursor: 'pointer',
  },
  main: { flex: 1 },
};

const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const displayName = user?.name || user?.email || 'Account';

  return (
    <div style={styles.container}>
      <nav style={styles.nav}>
        <div style={styles.navLeft}>
          <Link to="/dashboard" style={styles.logo}>coursemachine.app</Link>
          <div style={styles.navLinks}>
            <NavLink
              to="/dashboard"
              style={({ isActive }) => ({ ...styles.link, ...(isActive ? styles.linkActive : {}) })}
            >
              Dashboard
            </NavLink>
            <NavLink
              to="/library"
              style={({ isActive }) => ({ ...styles.link, ...(isActive ? styles.linkActive : {}) })}
            >
              Library
            </NavLink>
          </div>
        </div>
        <div style={styles.account} ref={menuRef}>
          <button type="button" style={styles.accountBtn} onClick={() => setMenuOpen(o => !o)}>
            {displayName} &#9662;
          </button>
          {menuOpen && (
            <div style={styles.menu}>
              <Link
                to="/settings/account"
                style={styles.menuItem}
                onClick={() => setMenuOpen(false)}
              >
                Account settings
              </Link>
              <button type="button" style={styles.menuItem} onClick={handleLogout}>
                Logout
              </button>
            </div>
          )}
        </div>
      </nav>
      <main style={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
